import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../../auth/components/AuthLayout';
import PasswordField from '../../auth/components/PasswordField';
import { useAuth } from '../../../context/AuthContext';
import { isSuperAdmin } from '../../../utils/userRoles';
import PageShell from '../../../components/layout/PageShell';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const SuperAdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({
    identifier: '',
    password: '',
  });
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('');
    setIsSubmitting(true);

    try {
      const payload = await login({
        provider: 'password',
        identifier: form.identifier,
        password: form.password,
        roleType: 'superadmin',
      });

      if (!isSuperAdmin(payload?.user)) {
        setStatus('This account does not have super admin access.');
        return;
      }

      navigate('/sa/dashboard', { replace: true });
    } catch (error) {
      setStatus(error.message || 'Unable to log in.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell containerClassName="p-0">
    <AuthLayout
      title="Super admin sign in"
      subtitle="Access platform-level controls for CrickArena."
      showLogo
    >
      <form onSubmit={handleSubmit} className="d-grid gap-3">
        <div>
          <label className="form-label" htmlFor="superAdminIdentifier">
            Email or phone number
          </label>
          <input
            id="superAdminIdentifier"
            name="identifier"
            type="text"
            className="form-control"
            placeholder="you@example.com or +1 555 123 4567"
            value={form.identifier}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <div className="d-flex justify-content-between align-items-center">
            <label className="form-label mb-0" htmlFor="superAdminPassword">
              Password
            </label>
            <Link to="/auth/forgot-password" className="small">
              Forgot password?
            </Link>
          </div>
          <PasswordField
            id="superAdminPassword"
            name="password"
            placeholder="Enter your password"
            value={form.password}
            onChange={handleChange}
            required
          />
        </div>

        {status && (
          <div className="alert alert-info py-2 mb-0" role="status">
            {status}
          </div>
        )}

        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Signing in...' : 'Sign in as super admin'}
        </button>

        <div className="text-center small">
          Need a regular account login? <Link to="/login">Go to user sign in</Link>
        </div>
      </form>
    </AuthLayout>
  
    </PageShell>
  );
};

export default SuperAdminLoginPage;
